import type { NLPSearchResultSource } from '../../ai-answer-dialog/types';

import { extractModelName } from './extract-model-name';
import { extractUniquePluginNames } from './extract-unique-plugin-names';
import { filterNonSearchMinions } from './filter-non-search-minions';
import { filterNonSearchPlugins } from './filter-non-search-plugins';
import { getRankedDocumentsWithChunks } from './get-ranked-documents-with-chunks';
import { getSearchResultsFromMinions } from './get-search-results-from-minions';
import { getSearchResultsFromPlugins } from './get-search-results-from-plugins';
import { mergeSearchExtraInfo } from './merge-search-extra-info';
import { normalizeCacheHit } from './normalize-cache-hit';
import { type NormalizedDebuggerData, type RawDebuggerData } from './types';
import { getOrchestrationRounds } from './utils';

// --- Main Normalization Function ---
/**
 * Normalizes all debugger data from raw extra attributes.
 * This is the single entry point for preparing data for all debugger panels.
 *
 * Search results are extracted from both plugin invocations and minion outputs,
 * with plugin data taking precedence when available.
 *
 * @param extraAttributes - Raw extra attributes from the AI Answer context
 * @param sources - Sources from commonAttributes.sources
 * @returns Normalized data object with all data needed for each debugger panel
 */
export const normalizeDebuggerData = (
	extraAttributes: RawDebuggerData | undefined,
	sources?: NLPSearchResultSource[],
): NormalizedDebuggerData => {
	const {
		originalQuery,
		transformedQuery,
		traceId,
		answerGenerationModel,
		cacheInfo,
		sources: extraSources = [],
		pluginInvocations = [],
		minion_outputs = [],
		rawJson,
	} = extraAttributes ?? {};

	const resolvedSources = sources !== undefined ? sources : extraSources;

	// Extract search data from both plugins and minions, then merge
	const pluginSearchInfo = getSearchResultsFromPlugins(pluginInvocations);
	const minionSearchInfo = getSearchResultsFromMinions(minion_outputs);
	const searchExtraInfo = mergeSearchExtraInfo(pluginSearchInfo, minionSearchInfo);
	const rankedDocumentsWithChunks = getRankedDocumentsWithChunks(searchExtraInfo);
	const orchestrationRounds = getOrchestrationRounds(minion_outputs);

	return {
		requestOverview: {
			originalQuery: originalQuery ?? null,
			transformedQuery: transformedQuery ?? null,
			traceId: traceId ?? null,
			modelName: extractModelName(answerGenerationModel),
			cacheHit: normalizeCacheHit(cacheInfo),
			sources: resolvedSources,
			invokedPluginNames: extractUniquePluginNames(pluginInvocations),
		},
		rankedSearch: {
			searchExtraInfo,
			rankedDocumentsWithChunks,
		},
		rawSearch: {
			searchResults: searchExtraInfo?.searchResults ?? [],
		},
		pluginInvocations: {
			allPluginInvocations: pluginInvocations,
			nonSearchPluginInvocations: filterNonSearchPlugins(pluginInvocations),
			hasPluginInvocations: pluginInvocations ? pluginInvocations.length > 0 : false,
		},
		minions: {
			allMinionOutputs: minion_outputs,
			nonSearchMinionOutputs: filterNonSearchMinions(minion_outputs),
			hasMinionOutputs: minion_outputs ? minion_outputs.length > 0 : false,
			orchestrationRounds,
			roundCount: orchestrationRounds.length,
		},
		rawJson: {
			rawJson,
		},
	};
};
